// Publish Types — the hard-coded, system-locked list of 6 user-facing publishing
// types surfaced in the Composer "Push to Publisher" picker and the Publisher
// page. Each Publish Type maps to a `destination_format` enum value which
// drives the writing-shape system prompt in `content-prepare-publication`.
//
// This is intentionally NOT a database table — it's a fixed system list.
// Per-workspace customization happens at the destinations layer (connectors +
// `workspace_manual_destinations`), not the type layer.

import type { ContentDestinationFormat } from "@/lib/composerCore";

export type PublishTypeKey =
  | "article"
  | "newsletter"
  | "long_social"
  | "book_chapter"
  | "content_segment";

export type PublishTypeDef = {
  key: PublishTypeKey;
  label: string;
  description: string;
  destinationFormat: ContentDestinationFormat;
};

export const PUBLISH_TYPES: readonly PublishTypeDef[] = [
  {
    key: "article",
    label: "Article / Blog Post",
    description: "Long-form article — full headings, lists, blockquotes.",
    destinationFormat: "markdown",
  },
  {
    key: "newsletter",
    label: "Newsletter",
    description: "Email-shaped: personal hook, short paragraphs, ~600–900 words.",
    destinationFormat: "newsletter",
  },
  {
    key: "long_social",
    label: "Long Social Post",
    description: "Numbered thread — each part under 280 characters.",
    destinationFormat: "social_thread",
  },
  {
    key: "book_chapter",
    label: "Book Chapter",
    description: "Long-form chapter — scene/section structure, paginated footnotes.",
    destinationFormat: "book_chapter",
  },
  {
    key: "content_segment",
    label: "Content Segment",
    description: "Plain text — no markdown, blank-line paragraphs.",
    destinationFormat: "plaintext",
  },
] as const;

export const PUBLISH_TYPE_BY_KEY: Record<PublishTypeKey, PublishTypeDef> =
  Object.fromEntries(PUBLISH_TYPES.map((t) => [t.key, t])) as Record<PublishTypeKey, PublishTypeDef>;

export const isPublishTypeKey = (value: unknown): value is PublishTypeKey =>
  typeof value === "string" && value in PUBLISH_TYPE_BY_KEY;

export const ALL_PUBLISH_TYPE_KEYS: readonly PublishTypeKey[] = PUBLISH_TYPES.map((t) => t.key);

/** Default applies-to publish types per connector type. Used to filter
 *  connector chips on the Publisher. `webhook` is generic so it applies
 *  everywhere. */
export const CONNECTOR_DEFAULT_PUBLISH_TYPES: Record<string, readonly PublishTypeKey[]> = {
  webhook: ALL_PUBLISH_TYPE_KEYS,
  email: ["article", "newsletter"],
  kit: ["newsletter"],
  bento: ["newsletter"],
  beehiiv: ["newsletter"],
  buttondown: ["newsletter"],
  wordpress: ["article", "book_chapter"],
  wordpress_self_hosted: ["article", "book_chapter"],
  ghost: ["article", "newsletter", "book_chapter"],
  substack_unofficial: ["article", "newsletter"],
};
